import React from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import type { CompanyQuestion } from '../../../generated-src/client/interview-settings/api';
import { apiService } from '../../services/apiService';

// Глобальное хранилище для CompanyQuestionsSection
const companyGlobalTexts: { [key: string]: string } = {};

interface QuestionInputProps {
  id: string;
  index: number;
  initialQuestion: string;
  initialAnswer: string;
  onRemove: (id: string) => void;
}

const QuestionInput: React.FC<QuestionInputProps> = React.memo(({ 
  id, 
  index, 
  initialQuestion, 
  initialAnswer,
  onRemove
}) => {
  const colors = useBrandingColors();
  const questionKey = `${id}-question`;
  const answerKey = `${id}-answer`;
  
  const [question, setQuestion] = React.useState(() => {
    return companyGlobalTexts[questionKey] || initialQuestion;
  });
  
  const [answer, setAnswer] = React.useState(() => {
    return companyGlobalTexts[answerKey] || initialAnswer;
  });

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setQuestion(newText);
    companyGlobalTexts[questionKey] = newText;
    adjustTextareaHeight(e.target);
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setAnswer(newText);
    companyGlobalTexts[answerKey] = newText;
    adjustTextareaHeight(e.target);
  };

  // Автоматически подстраиваем высоту при монтировании компонента
  React.useEffect(() => {
    const questionTextarea = document.querySelector(`textarea[data-id="${questionKey}"]`) as HTMLTextAreaElement;
    const answerTextarea = document.querySelector(`textarea[data-id="${answerKey}"]`) as HTMLTextAreaElement;
    
    if (questionTextarea && question) {
      adjustTextareaHeight(questionTextarea);
    }
    if (answerTextarea && answer) {
      adjustTextareaHeight(answerTextarea);
    }
  }, [questionKey, answerKey, question, answer]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-1">
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-[#525866]">
              Вопрос {index + 1}
            </label>
            <button
              onClick={() => onRemove(id)}
              className="text-sm font-medium transition-colors"
              style={{ color: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.color = colors.primaryHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = colors.primary; }}
            >
              Удалить
            </button>
          </div>
          <textarea
            data-id={questionKey}
            value={question}
            onChange={handleQuestionChange}
            placeholder={`Введите вопрос ${index + 1}...`}
            className="w-full px-3 py-2 border border-[#cdd0d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent resize-none overflow-hidden text-sm"
            rows={1}
            style={{ minHeight: '44px' }}
          />
        </div>
      </div>
      
      <div className="flex items-start gap-3 p-1">
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-[#525866]">
              Ответ {index + 1}
            </label>
          </div>
          <textarea
            data-id={answerKey}
            value={answer}
            onChange={handleAnswerChange}
            placeholder={`Введите ответ на вопрос ${index + 1}...`}
            className="w-full px-3 py-2 border border-[#cdd0d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent resize-none overflow-hidden text-sm"
            rows={1}
            style={{ minHeight: '44px' }}
          />
        </div>
      </div>
    </div>
  );
});

interface Props {
  embedded?: boolean;
}

export const CompanyQuestionsSection: React.FC<Props> = ({ embedded = false }) => {
  const [questions, setQuestions] = React.useState<Array<{ id: string; question: string; answer: string }>>([]);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const colors = useBrandingColors();

  React.useEffect(() => {
    loadCompanyQuestions();
  }, []);

  const loadCompanyQuestions = async () => {
    try {
      const response = await apiService.getCompanyQuestions();
      const apiQuestions = response.questions || [];
      const items = apiQuestions.map((q: CompanyQuestion, i: number) => ({
        id: `question-${i}-${Date.now()}`,
        question: q.question || '',
        answer: q.answer || ''
      }));
      
      setQuestions(items.length > 0 ? items : [{ id: `question-0-${Date.now()}`, question: '', answer: '' }]);
    } catch (error) {
      setError('Ошибка при загрузке вопросов о компании');
      setQuestions([{ id: `question-0-${Date.now()}`, question: '', answer: '' }]);
    }
  };

  const handleAdd = React.useCallback(() => {
    const newId = `question-${questions.length}-${Date.now()}`;
    setQuestions(prev => [...prev, { id: newId, question: '', answer: '' }]);
  }, [questions.length]);

  const handleRemove = React.useCallback((id: string) => {
    setQuestions(prev => {
      delete companyGlobalTexts[`${id}-question`];
      delete companyGlobalTexts[`${id}-answer`];
      return prev.filter(q => q.id !== id);
    });
  }, []);

  const handleSave = React.useCallback(async () => {
    try {
      setError('');
      setSaving(true);
      
      const actualQuestions = questions.map(q => ({
        question: companyGlobalTexts[`${q.id}-question`] || q.question || '',
        answer: companyGlobalTexts[`${q.id}-answer`] || q.answer || ''
      }));
      
      const filtered = actualQuestions.filter(q => q.question.trim().length > 0 || q.answer.trim().length > 0);
      const companyQuestionsData: CompanyQuestion[] = filtered.map((q, i) => ({
        question: q.question.trim(),
        answer: q.answer.trim(),
        orderIndex: i
      }));

      await apiService.updateCompanyQuestions({ questions: companyQuestionsData });
      setError('');
    } catch (error) {
      setError('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  }, [questions]);

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    embedded ? <>{children}</> : <div className="bg-white rounded-lg border border-[#e2e4e9] p-6">{children}</div>
  );

  return (
    <Wrapper>
      {!embedded && <h2 className="text-xl font-semibold text-[#000] mb-6">Вопросы о компании</h2>}

      <div className="space-y-6 mb-6">
        {questions.length === 0 && (
          <button
            onClick={handleAdd}
            className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-[14px] py-[10px] relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer h-[44px]"
          >
            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
              <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                  Добавить вопрос
                </p>
              </div>
            </div>
          </button>
        )}

        {questions.map((item, idx) => (
          <QuestionInput
            key={item.id}
            id={item.id}
            index={idx}
            initialQuestion={item.question}
            initialAnswer={item.answer}
            onRemove={handleRemove}
          />
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        {questions.length > 0 && (
          <div className="flex gap-3">
            <button
              onClick={handleAdd}
              className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-[14px] py-[10px] relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer h-[44px]"
            >
              <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                  <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                    Добавить вопрос
                  </p>
                </div>
              </div>
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-[10px] text-white rounded-2xl transition-colors h-[44px]"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
              disabled={saving}
            >
              {saving ? 'Сохраняем...' : 'Сохранить'}
            </button>
          </div>
        )}
        {questions.length === 0 && (
          <button
            onClick={handleSave}
            className="px-6 py-[10px] text-white rounded-2xl transition-colors h-[44px]"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        )}
      </div>
    </Wrapper>
  );
};


